/**
 * @component ry-tabs
 * @category  nav
 * @summary   Horizontal (or vertical) tabbed interface (Fomantic UI `.ui.tabular.menu` + tab system). Authored as `<ry-tab title="X" active?>...` and transformed at setup. Exactly one tab is active at any time; the auto-activate-on-arrow-key behavior follows WAI-ARIA's "automatic activation" pattern.
 *
 * Reference: Fomantic UI's tab module. ry-tabs auto-activates on arrow keys
 * (the activation follows focus). Manual-activation tabs (where Arrow only
 * moves focus and Enter activates) are NOT supported — by design, since
 * follow-focus is the more common pattern for instant tab content.
 *
 * @attribute vertical    boolean   default=false   Stack tabs vertically as a left-side rail instead of a horizontal strip.
 * @attribute fluid       boolean   default=false   Tab list stretches to fill container width; triggers share width equally.
 *
 * @property  selectedIndex   number   Index of the currently active tab. Returns -1 if no tab is active (transient — setup() guarantees one is active).
 *
 * @method    select(index)   Activate the tab at `index`. No-op if index is out of range. Fires `ry:change` on success.
 *
 * @event ry:beforechange
 *        detail: { index: number, trigger: HTMLElement, panel: HTMLElement, previous: number }
 *        fires:  at the top of `select()` — click, arrow keys, Home/End, or `el.select(i)` — before any tab is (de)activated. `index` is the tab ABOUT to activate, `previous` the current `selectedIndex`.
 *        cancelable: preventDefault() leaves the current tab active; `ry:change` does not fire. The unsaved-changes-on-tab-switch guard.
 *
 * @event ry:change
 *        detail: { index: number, trigger: HTMLElement, panel: HTMLElement }
 *        fires:  after the selected tab transitions to active. Includes the *new* index, not the previous one.
 *
 * @keyboard  ArrowLeft / ArrowRight — focus and activate the previous / next tab (wraps). Home — focus and activate first tab. End — focus and activate last tab. Enter / Space — activate the focused tab (default browser behavior on `<button>` triggers).
 *
 * @state     Per-panel `data-ry-state` of `"active"` or `""` (empty). Per-trigger `aria-selected="true"|"false"`.
 *
 * @content   `<ry-tab title="X" active?>` children. Each `<ry-tab>` becomes the panel; the title attribute becomes the trigger button. Tabs without `title` fall back to `"Tab N"`.
 *
 * @dom
 *   ry-tabs[vertical?][fluid?]
 *     > div.ry-tabs__list    [data-ry-target="list"]   role="tablist"
 *       > button.ry-tabs__trigger    [data-ry-target="trigger"]   role="tab"
 *           aria-selected="true|false"  aria-controls=panel-id  tabindex="0|-1"
 *       > button.ry-tabs__trigger    (additional triggers follow)
 *     > ry-tab.ry-tabs__panel        [data-ry-target="panel"]    role="tabpanel"
 *           aria-labelledby=trigger-id  data-ry-state="active|"
 *
 * @gotcha  `<ry-tab>` children are NOT replaced (unlike `<ry-accordion-item>`). They are KEPT in DOM and decorated with `data-ry-target="panel"`, `role="tabpanel"`, and class hooks. Use `[data-ry-target="panel"]` OR `ry-tab` as the panel selector.
 *
 * @gotcha  Arrow keys auto-activate (follow-focus). Some WAI-ARIA tab patterns recommend manual activation (Arrow moves focus, Enter activates). ry-tabs does NOT support that mode — by design. If you need to defer activation (e.g. expensive panels), gate work inside the `ry:change` handler.
 *
 * @gotcha  If you mount `<ry-tabs>` with zero `<ry-tab>` children, the tab list is not created and `select()` is a no-op. setup() does not throw — the element is effectively inert. Insert tabs BEFORE mount.
 *
 * @gotcha  `<ry-tab active>` only honored at setup time. Adding the `active` attribute to a `<ry-tab>` post-mount does not select that tab. Use `el.select(index)`.
 *
 * @example basic
 * ```html
 * <ry-tabs>
 *   <ry-tab title="Overview" active>Welcome.</ry-tab>
 *   <ry-tab title="Details">More info.</ry-tab>
 *   <ry-tab title="Pricing">Plans.</ry-tab>
 * </ry-tabs>
 * ```
 *
 * @example pre-selected
 * ```html
 * <ry-tabs>
 *   <ry-tab title="A">A</ry-tab>
 *   <ry-tab title="B" active>B (starts active)</ry-tab>
 *   <ry-tab title="C">C</ry-tab>
 * </ry-tabs>
 * ```
 *
 * @example fluid
 * ```html
 * <ry-tabs fluid>
 *   <ry-tab title="One">A</ry-tab>
 *   <ry-tab title="Two">B</ry-tab>
 *   <ry-tab title="Three">C</ry-tab>
 * </ry-tabs>
 * ```
 *
 * @example vertical
 * ```html
 * <ry-tabs vertical>
 *   <ry-tab title="Profile">Profile</ry-tab>
 *   <ry-tab title="Account">Account</ry-tab>
 *   <ry-tab title="Notifications">Notifications</ry-tab>
 * </ry-tabs>
 * ```
 *
 * @tutorial click-selects
 * ```js
 * const el = mount(`
 *   <ry-tabs>
 *     <ry-tab title="A">A</ry-tab>
 *     <ry-tab title="B">B</ry-tab>
 *   </ry-tabs>
 * `);
 * const triggers = el.querySelectorAll('[data-ry-target="trigger"]');
 * await expectEqual(el.selectedIndex, 0, 'first tab active by default');
 * await expectEvent(el, 'ry:change', () => triggers[1].click());
 * await expectEqual(el.selectedIndex, 1, 'click selected B');
 * await expectAttr(triggers[0], 'aria-selected', 'false');
 * await expectAttr(triggers[1], 'aria-selected', 'true');
 * ```
 *
 * @tutorial active-attribute-pre-selects
 * ```js
 * // Wrong assumption: tabs always start with the first one active. Tabs
 * // with [active] on a non-first <ry-tab> are honored at setup.
 * const el = mount(`
 *   <ry-tabs>
 *     <ry-tab title="A">A</ry-tab>
 *     <ry-tab title="B" active>B</ry-tab>
 *     <ry-tab title="C">C</ry-tab>
 *   </ry-tabs>
 * `);
 * await expectEqual(el.selectedIndex, 1, '[active] on second tab makes it the initial selection');
 * ```
 *
 * @tutorial keyboard-arrow-roving-and-activation
 * ```js
 * // Wrong assumption: arrows only move focus. They ALSO auto-activate the
 * // tab (follow-focus pattern). Verify both focus and activation transitions.
 * const el = mount(`
 *   <ry-tabs>
 *     <ry-tab title="A">A</ry-tab>
 *     <ry-tab title="B">B</ry-tab>
 *     <ry-tab title="C">C</ry-tab>
 *   </ry-tabs>
 * `);
 * const triggers = el.querySelectorAll('[data-ry-target="trigger"]');
 * triggers[0].focus();
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(document.activeElement, triggers[1], 'ArrowRight moves focus');
 * await expectEqual(el.selectedIndex, 1, 'and auto-activates');
 * // Wrap from last to first
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'End', bubbles: true }));
 * await expectEqual(el.selectedIndex, 2, 'End jumps to last');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(el.selectedIndex, 0, 'ArrowRight from last wraps to first');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowLeft', bubbles: true }));
 * await expectEqual(el.selectedIndex, 2, 'ArrowLeft from first wraps to last');
 * ```
 *
 * @tutorial roving-tabindex
 * ```js
 * // Only the active trigger has tabindex="0"; others have tabindex="-1".
 * // This is the roving-tabindex pattern — Tab key enters/exits the group
 * // without cycling through every tab.
 * const el = mount(`
 *   <ry-tabs>
 *     <ry-tab title="A">A</ry-tab>
 *     <ry-tab title="B" active>B</ry-tab>
 *   </ry-tabs>
 * `);
 * const triggers = el.querySelectorAll('[data-ry-target="trigger"]');
 * await expectAttr(triggers[0], 'tabindex', '-1', 'inactive tab is not in tab order');
 * await expectAttr(triggers[1], 'tabindex', '0', 'active tab is the entry point');
 * el.select(0);
 * await expectAttr(triggers[0], 'tabindex', '0', 'newly active becomes the entry point');
 * await expectAttr(triggers[1], 'tabindex', '-1', 'previously active steps out');
 * ```
 *
 * @tutorial programmatic-select
 * ```js
 * const el = mount(`
 *   <ry-tabs>
 *     <ry-tab title="A">A</ry-tab>
 *     <ry-tab title="B">B</ry-tab>
 *   </ry-tabs>
 * `);
 * const events = [];
 * el.addEventListener('ry:change', (e) => events.push(e.detail.index));
 * el.select(1);
 * await expectEqual(el.selectedIndex, 1);
 * await expectEqual(events.length, 1, 'fires ry:change');
 * await expectEqual(events[0], 1, 'detail.index reflects new selection');
 * // Out-of-range is silent no-op
 * el.select(99);
 * await expectEqual(el.selectedIndex, 1, 'out-of-range index is ignored');
 * await expectEqual(events.length, 1, 'no extra ry:change');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // The unsaved-changes guard: preventDefault() on ry:beforechange keeps the
 * // current tab active, whatever asked for the switch (click, arrows, select()).
 * const el = mount(`
 *   <ry-tabs>
 *     <ry-tab title="A">A</ry-tab>
 *     <ry-tab title="B">B</ry-tab>
 *   </ry-tabs>
 * `);
 * const triggers = el.querySelectorAll('[data-ry-target="trigger"]');
 * const changed = [];
 * el.addEventListener('ry:change', () => changed.push(1));
 * el.addEventListener('ry:beforechange', (e) => {
 *   if (e.detail.index === 1 && e.detail.previous === 0) e.preventDefault();
 * }, { once: true });
 * triggers[1].click();
 * await expectEqual(el.selectedIndex, 0, 'vetoed switch leaves A active');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * await expectAttr(triggers[1], 'aria-selected', 'false');
 * triggers[1].click();
 * await expectEqual(el.selectedIndex, 1, 'without the veto, the click switches to B');
 * await expectEqual(changed.length, 1, 'ry:change fired once');
 * ```
 *
 * @tutorial aria-controls-and-labelledby-linkage
 * ```js
 * // Verify the trigger <-> panel ARIA pairing.
 * const el = mount(`
 *   <ry-tabs id="t">
 *     <ry-tab title="A">A</ry-tab>
 *     <ry-tab title="B">B</ry-tab>
 *   </ry-tabs>
 * `);
 * const triggers = el.querySelectorAll('[data-ry-target="trigger"]');
 * const panels = el.querySelectorAll('[data-ry-target="panel"]');
 * await expectAttr(triggers[0], 'aria-controls', panels[0].id);
 * await expectAttr(panels[0], 'aria-labelledby', triggers[0].id);
 * await expectAttr(triggers[1], 'aria-controls', panels[1].id);
 * await expectAttr(panels[1], 'aria-labelledby', triggers[1].id);
 * ```
 *
 * @see ry-accordion
 * @see ry-dropdown
 */

import { RyElement } from '../core/ry-element.js';

interface TabChangeDetail {
  index: number;
  trigger: HTMLElement;
  panel: HTMLElement;
}

interface TabBeforeChangeDetail extends TabChangeDetail {
  previous: number;
}

export class RyTabs extends RyElement {
  setup(): void {
    // Transform minimal markup to full structure
    this.#transformMinimalMarkup();

    // Bind click handlers
    this.$$('[data-ry-target="trigger"]').forEach(trigger => {
      this.on(trigger, 'click', this.#handleTriggerClick);
    });

    // Keyboard navigation
    this.on(this, 'keydown', this.#handleKeydown);

    // Initialize ARIA
    this.#initAria();
  }

  #transformMinimalMarkup(): void {
    const tabs = this.$$('ry-tab');
    if (tabs.length === 0) return;

    const activeIndex = tabs.findIndex(t => t.hasAttribute('active'));
    const defaultActive = activeIndex === -1 ? 0 : activeIndex;

    // Build tab list
    // data-ry-target for JS queries, class for CSS styling
    const tabList = document.createElement('div');
    tabList.setAttribute('data-ry-target', 'list');
    tabList.className = 'ry-tabs__list';
    tabList.setAttribute('role', 'tablist');

    tabs.forEach((tab, index) => {
      const title = tab.getAttribute('title') ?? `Tab ${index + 1}`;
      const isActive = index === defaultActive;

      const trigger = document.createElement('button');
      trigger.setAttribute('data-ry-target', 'trigger');
      trigger.className = 'ry-tabs__trigger';
      trigger.setAttribute('role', 'tab');
      trigger.setAttribute('aria-selected', String(isActive));
      trigger.textContent = title;

      tabList.appendChild(trigger);

      // Convert ry-tab to panel
      tab.setAttribute('data-ry-target', 'panel');
      tab.className = 'ry-tabs__panel';
      tab.setAttribute('role', 'tabpanel');
      (tab as HTMLElement).dataset.ryState = isActive ? 'active' : '';
      tab.removeAttribute('title');
      tab.removeAttribute('active');
    });

    // Insert tab list at beginning
    this.insertBefore(tabList, this.firstChild);
  }

  #initAria(): void {
    const triggers = this.$$<HTMLElement>('[data-ry-target="trigger"]');
    const panels = this.$$<HTMLElement>('[data-ry-target="panel"], ry-tab');

    triggers.forEach((trigger, index) => {
      const panel = panels[index];
      if (!panel) return;

      // Generate IDs
      const triggerId = trigger.id || `ry-tab-${this.id || ''}-${index}`;
      const panelId = panel.id || `ry-tabpanel-${this.id || ''}-${index}`;

      trigger.id = triggerId;
      panel.id = panelId;

      trigger.setAttribute('aria-controls', panelId);
      panel.setAttribute('aria-labelledby', triggerId);

      // Set tabindex for keyboard nav
      trigger.setAttribute('tabindex', trigger.getAttribute('aria-selected') === 'true' ? '0' : '-1');
    });

    // Ensure one tab is active
    const activeIndex = triggers.findIndex(t => t.getAttribute('aria-selected') === 'true');
    if (activeIndex === -1 && triggers.length > 0) {
      this.select(0);
    }
  }

  #handleTriggerClick = (event: Event): void => {
    const trigger = event.currentTarget as HTMLElement;
    const triggers = this.$$<HTMLElement>('[data-ry-target="trigger"]');
    const index = triggers.indexOf(trigger);

    if (index !== -1) {
      this.select(index);
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    const triggers = this.$$<HTMLElement>('[data-ry-target="trigger"]');
    const currentIndex = triggers.indexOf(document.activeElement as HTMLElement);

    if (currentIndex === -1) return;

    let newIndex: number;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        newIndex = (currentIndex + 1) % triggers.length;
        this.select(newIndex);
        triggers[newIndex]?.focus();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = (currentIndex - 1 + triggers.length) % triggers.length;
        this.select(newIndex);
        triggers[newIndex]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        this.select(0);
        triggers[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        this.select(triggers.length - 1);
        triggers[triggers.length - 1]?.focus();
        break;
    }
  };

  select(index: number): void {
    const triggers = this.$$<HTMLElement>('[data-ry-target="trigger"]');
    const panels = this.$$<HTMLElement>('[data-ry-target="panel"], ry-tab');

    if (index < 0 || index >= triggers.length) return;

    const nextTrigger = triggers[index];
    const nextPanel = panels[index];
    if (!nextTrigger || !nextPanel) return;
    if (!this.emit<TabBeforeChangeDetail>('beforechange', {
      index, trigger: nextTrigger, panel: nextPanel, previous: this.selectedIndex,
    })) return;

    // Deactivate all
    triggers.forEach(trigger => {
      trigger.setAttribute('aria-selected', 'false');
      trigger.setAttribute('tabindex', '-1');
    });

    panels.forEach(panel => {
      panel.dataset.ryState = '';
      panel.removeAttribute('active');
    });

    // Activate selected
    const trigger = triggers[index];
    const panel = panels[index];

    if (trigger && panel) {
      trigger.setAttribute('aria-selected', 'true');
      trigger.setAttribute('tabindex', '0');
      panel.dataset.ryState = 'active';

      this.emit<TabChangeDetail>('change', { index, trigger, panel });
    }
  }

  get selectedIndex(): number {
    const triggers = this.$$('[data-ry-target="trigger"]');
    return triggers.findIndex(t => t.getAttribute('aria-selected') === 'true');
  }
}

customElements.define('ry-tabs', RyTabs);

// Register placeholder for minimal markup
if (!customElements.get('ry-tab')) {
  customElements.define('ry-tab', class extends HTMLElement {});
}
